import { Flex, Text } from "@chakra-ui/react"
import { useRouter } from "next/router"
import { useEffect } from "react"
import { useRecoilValue, useSetRecoilState } from "recoil"
import { Suggestion } from "../../../../api-clients/suggest-openapi"
import { fhAnalyticsUserActions, pages } from "../../../../common/constants/constants"
import { useBrandedTranslation } from "../../../../common/hooks/useBrandedTranslation"
import sendFhAnalytics from "../../../../common/lib/fredhopper"
import {
  onboardingShouldOpenSearchState,
  onboardingStepIndexState,
  suggestionsStepIndexState,
} from "../../../../common/store/onboardingStore"
import { redirectToPage } from "../../../../common/utils/router-utils"
import { specialFhValue } from "../../../../common/utils/spare-parts-utils"
import CustomImage from "../../../shared/CustomImage"
import SuggestionRow from "../SuggestionRow"

type Props = {
  suggestions: Array<Suggestion>
  handleResetSearchField: () => void
}

const SparePartSuggestions = ({ suggestions, handleResetSearchField }: Props) => {
  const { t } = useBrandedTranslation()
  const router = useRouter()

  const shouldOpenSearch = useRecoilValue(onboardingShouldOpenSearchState)
  const suggestionsStepIndex = useRecoilValue(suggestionsStepIndexState)
  const setOnboardingStepIndex = useSetRecoilState(onboardingStepIndexState)

  useEffect(() => {
    if (Array.isArray(suggestions) && suggestions.length !== 0) {
      if (shouldOpenSearch && (suggestionsStepIndex >= 1 || suggestionsStepIndex <= 3)) {
        setOnboardingStepIndex(suggestionsStepIndex)
      }
    }
  }, [suggestionsStepIndex, shouldOpenSearch])

  /* Guard clause if no spare part suggestions */
  if (!Array.isArray(suggestions) || suggestions.length === 0) {
    return null
  }

  const handleClick = (sparePartId: string) => {
    //wait until analytics is sent as otherwise analytics call is cancelled
    sendFhAnalytics({
      locale: router.locale,
      action: fhAnalyticsUserActions.SUGG_SPTCLICK,
      itemId: sparePartId,
      shouldBeInterrupted: true,
    }).finally(() => {
      handleResetSearchField()
      redirectToPage(router, pages.SPARE_PART, sparePartId)
    })
  }

  return (
    <>
      {suggestions.map((sugg) => {
        // Extract the SPT Id from the attribute
        const sptId = sugg.secondId.replace("p_", "").toUpperCase()

        // Workaround for images until backend implements _thumbnail attribute
        const thumbnailSrc = sugg._imageurl.replace("Large", "Thumbnail").replace("Medium", "Thumbnail")

        return (
          <SuggestionRow key={sptId} onMouseDown={() => handleClick(sptId)}>
            <CustomImage src={thumbnailSrc} alt="spare part image" minW="10" h="10" mr="4" />
            <Flex w="100%" align="center" justify="space-between">
              <Flex flexDirection="column">
                <Text mb="1" className="suggestion-name">
                  {sugg.name}
                </Text>
                <Text mb="1.5">
                  {specialFhValue(sugg.year_manufacture_from)}-{specialFhValue(sugg.year_manufacture_until)}
                </Text>
                {!!sugg.spt_spare_parts_counts && (
                  <Text fontSize="2xs" color="gray.700">
                    {sugg.spt_spare_parts_counts} {t("searchTerm-sparePart", { count: +sugg.spt_spare_parts_counts })}
                  </Text>
                )}
              </Flex>
            </Flex>
          </SuggestionRow>
        )
      })}
    </>
  )
}

export default SparePartSuggestions
